import { z } from "zod";

/**
 * Community copy for the deck.
 *
 * This is the one content module not yet pulled from Sanity. The matching
 * Studio schema is in `sanity/schemas/community-copy.ts`; once Leonie has
 * authored the document, `npm run content:pull` will carry it into the
 * snapshot and the literal below is replaced by
 * `communityCopySchema.parse(snapshot.communityCopy)` — a one-line change,
 * because the schema is already the same shape.
 *
 * Until then it is still parsed at module load, so a broken edit here fails
 * the build rather than reaching a visitor. Match it in tests through the
 * exported object, never as a string literal.
 *
 * Leonie's admin portal is interface chrome, not editorial copy, so its
 * wording deliberately stays in the component.
 */

const shortText = z.string().trim().min(1).max(180);
const bodyText = z.string().trim().min(1).max(800);

const houseRuleSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Gebruik een stabiele kebab-case ID."),
  title: shortText,
  body: bodyText,
});

const panelSchema = z.object({
  eyebrow: shortText,
  title: shortText,
  body: bodyText,
});

export const communityCopySchema = z.object({
  /** The invitation, shown in the room to someone who is not a member yet. */
  invitation: panelSchema.extend({
    ctaLabel: shortText,
    signInLabel: shortText,
  }),
  join: z.object({
    title: shortText,
    intro: bodyText,
    nameLabel: shortText,
    nameHint: shortText,
    emailLabel: shortText,
    emailHint: shortText,
    introLabel: shortText,
    introHint: bodyText,
    introPlaceholder: shortText,
    houseRulesLabel: shortText,
    ageLabel: shortText,
    submitLabel: shortText,
    privacyNote: bodyText,
  }),
  linkSent: panelSchema.extend({
    codeLabel: shortText,
    codeHint: bodyText,
    codeSubmitLabel: shortText,
  }),
  pending: panelSchema,
  declined: panelSchema,
  suspended: panelSchema,
  welcome: panelSchema.extend({
    membersHeading: shortText,
  }),
  fire: z.object({
    composerLabel: shortText,
    composerPlaceholder: shortText,
    sendLabel: shortText,
    emptyChannel: bodyText,
    deletedMessage: shortText,
    withdrawLabel: shortText,
    moderateLabel: shortText,
    readOnlyNotice: bodyText,
    channelNavLabel: shortText,
  }),
  signIn: z.object({
    title: shortText,
    body: bodyText,
    emailLabel: shortText,
    submitLabel: shortText,
    joinInsteadLabel: shortText,
  }),
  houseRules: z.object({
    title: shortText,
    intro: bodyText,
    rules: houseRuleSchema.array().min(1),
  }),
});

export type CommunityCopy = z.infer<typeof communityCopySchema>;

export const communityCopy: CommunityCopy = communityCopySchema.parse({
  invitation: {
    eyebrow: "Buiten, om het vuur",
    title: "Het buitendek",
    body: "Achter de shack ligt een vuurplaats in de duinen. Daar zit een kleine groep mensen die elkaar op weg helpt. Je komt er niet zomaar binnen: Leonie leest eerst even wie je bent.",
    ctaLabel: "Sluit je aan bij de cirkel",
    signInLabel: "Ik ben al lid",
  },
  join: {
    title: "Kom bij het vuur zitten",
    intro: "Vertel kort wie je bent en wat je hier brengt. Leonie leest het zelf en laat je weten of je erbij kunt. Meestal binnen een paar dagen.",
    nameLabel: "Hoe mogen we je noemen?",
    nameHint: "Je voornaam is genoeg. Dit is de naam die andere leden zien.",
    emailLabel: "E-mailadres",
    emailHint: "Je krijgt een link om binnen te komen. Geen wachtwoord nodig.",
    introLabel: "Wat brengt jou hier?",
    introHint: "Een paar zinnen zijn genoeg. Dit lezen alleen Leonie en niemand anders — het komt niet in de groep te staan.",
    introPlaceholder: "Ik ben hier omdat…",
    houseRulesLabel: "Ik heb de huisregels gelezen en houd me eraan",
    ageLabel: "Ik ben 18 jaar of ouder",
    submitLabel: "Verstuur mijn aanmelding",
    privacyNote: "We bewaren je naam, e-mailadres en deze introductie om je aanmelding te kunnen beoordelen. Je kunt je later altijd laten verwijderen.",
  },
  linkSent: {
    eyebrow: "Kijk in je mail",
    title: "Er ligt een link voor je klaar",
    body: "We hebben je een mail gestuurd. Klik op de link erin, dan ben je binnen. Niets ontvangen? Kijk even in je spam, of probeer het over een paar minuten opnieuw.",
    codeLabel: "Of vul de code uit de mail in",
    codeHint: "Lees je mail op een andere telefoon of computer dan deze? Dan werkt de link daar niet. In dezelfde mail staat een code van zes cijfers; die kun je hier invullen.",
    codeSubmitLabel: "Kom binnen",
  },
  pending: {
    eyebrow: "Je aanmelding staat genoteerd",
    title: "Het vuur wordt opgestookt",
    body: "Leonie heeft je introductie ontvangen en leest hem zelf. Zodra ze de deur voor je opendoet, krijg je bericht. Je hoeft hier niets meer te doen.",
  },
  declined: {
    eyebrow: "Deze keer niet",
    title: "De cirkel is nu niet de goede plek",
    body: "Leonie heeft je aanmelding gelezen en denkt dat de cirkel op dit moment niet past bij wat je zoekt. Dat zegt niets over jou. De shack zelf blijft gewoon open, en je bent altijd welkom om Leonie rechtstreeks iets te vragen.",
  },
  suspended: {
    eyebrow: "Even pauze",
    title: "Je plek bij het vuur staat op pauze",
    body: "Je toegang tot de cirkel is tijdelijk stilgezet. Neem contact op met Leonie als je wilt weten hoe dat komt of wat er nodig is om terug te komen.",
  },
  welcome: {
    eyebrow: "Je bent binnen",
    title: "Welkom op het dek",
    body: "Leonie heeft je aanmelding gelezen en de deur voor je opengezet. Het vuur brandt. Kom erbij zitten, of lees eerst een tijdje mee — dat mag hier ook.",
    membersHeading: "Wie er al zitten",
  },
  fire: {
    composerLabel: "Zeg iets",
    composerPlaceholder: "Schrijf wat er is…",
    sendLabel: "Plaats",
    emptyChannel: "Hier is het nog stil. Jij mag de eerste zijn — dat is minder eng dan het klinkt.",
    deletedMessage: "Dit bericht is weggehaald.",
    withdrawLabel: "Haal weg",
    moderateLabel: "Verwijder als beheerder",
    readOnlyNotice: "Op deze plek plaatst alleen Leonie berichten. Je kunt hier wel meelezen.",
    channelNavLabel: "Plekken op het dek",
  },
  signIn: {
    title: "Welkom terug",
    body: "Vul het e-mailadres in waarmee je je hebt aangemeld. Je krijgt een link waarmee je meteen binnen bent.",
    emailLabel: "E-mailadres",
    submitLabel: "Stuur me een link",
    joinInsteadLabel: "Ik ben nog geen lid",
  },
  houseRules: {
    title: "Huisregels",
    intro: "De cirkel werkt alleen als het er veilig is. Dit zijn geen voorwaarden maar afspraken; wie zich er niet aan houdt, gaat er weer uit.",
    rules: [
      {
        id: "vertrouwelijk",
        title: "Wat hier gezegd wordt, blijft hier",
        body: "Je deelt niets uit de cirkel buiten de cirkel. Geen screenshots, geen verhalen van een ander doorvertellen.",
      },
      {
        id: "geen-advies",
        title: "Luisteren gaat voor oplossen",
        body: "Iemand die iets deelt vraagt niet automatisch om raad. Vraag eerst wat iemand nodig heeft.",
      },
      {
        id: "geen-hulpverlening",
        title: "Dit is geen hulpverlening",
        body: "De cirkel is een plek voor gesprek en herkenning, geen therapie en geen crisisdienst. Heb je acuut hulp nodig, zoek die dan bij je huisarts of een hulplijn.",
      },
      {
        id: "geen-promotie",
        title: "Geen etalage",
        body: "Geen reclame, geen werving, geen links naar je eigen aanbod tenzij iemand erom vraagt.",
      },
      {
        id: "eigen-naam",
        title: "Kom als jezelf",
        body: "Eén account per persoon, onder een naam waaronder je aanspreekbaar bent.",
      },
    ],
  },
});
